import Vue from 'vue'
import upperFirst from 'lodash/upperFirst'
import camelCase from 'lodash/camelCase'
import moment from 'moment'

export default {

    // globally register all components in the 'src/components' directory (using kebab-case)
    registerComponents () {
        const requireComponent = require.context(
            // The relative path of the components folder
            '@/components',
            // Whether or not to look in subfolders
            true,
            // The regular expression used to match base component filenames
            /[A-Z]\w+\.(vue|js)$/
        )

        requireComponent.keys().forEach(fileName => {
            // Get component config
            const componentConfig = requireComponent(fileName)

            // Get PascalCase name of component
            const componentName = upperFirst(
                camelCase(
                    fileName
                        .split('/')
                        .pop()
                        .replace(/\.\w+$/, ''),
                )
            )

            const kebabName = componentName.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()

            // Register component globally
            Vue.component(
                kebabName,
                // Look for the component options on `.default`, which will
                // exist if the component was exported with `export default`,
                // otherwise fall back to module's root.
                componentConfig.default || componentConfig
            )
        })
    },

    // add a custom Vue mixin to allow a given service to be accessed from any
    // child component, e.g. as 'this.$i18n'
    addCustomMixin (name) {
        Vue.mixin({
            beforeCreate () {
                const options = this.$options
                if (options[name]) {
                    this['$' + name] = options[name]
                } else if (options.parent && options.parent['$' + name]) {
                    this['$' + name] = options.parent['$' + name]
                }
            }
        })
    },

    /**
     * Configure vue-resource to attach the current user auth_key to all api requests,
     * and to redirect to login page if a request is unauthorized (see BaseController.php::beforeAction)
     */
    addAuthorizationInterceptor (store, router, user) {
        Vue.http.interceptors.push(function (request, next) {
            request.headers.set('Authorization', store.state.currentUser.auth_key)

            next(function (response) {
                // NB: don't trigger a chain of logout requests
                if ((request.url.indexOf('logout') === -1) &&
                    (request.url.indexOf('twofa') === -1) &&
                    (!response.status || response.status === 401 || response.status === 403)) {
                    if (response.status === 401) {
                        user.logoutUser(Vue, store.state.currentUser.id)
                    } else if (response.status === 403) {
                        Vue.toasted.error('You are not authorized to perform this action')
                    }
                }
            })
        })
    },

    formatDate (d) {
        if (moment(d).isValid()) {
            return moment(d).format('MMMM D YYYY, h:mm A')
        } else {
            return ''
        }

    },

    formatDateShort (d) {
        if (moment(d).isValid()) {
            return moment(d).format('M/D/YY')
        } else {
            return ''
        }
    },

    formatDateSQL (d) {
        if (moment(d).isValid()) {
            return moment(d).format('YYYY-MM-DD')
        } else {
            return ''
        }
    },

    formatDateSQLTimestamp (d) {
        if (moment(d).isValid()) {
            return moment(d).format('YYYY-MM-DD HH:mm:ss')
        } else {
            return ''
        }
    },

    dateIsLessThanDaysAgo (d, days) {
        const ago = moment().subtract(days, 'days')
        return moment(d).isAfter(ago)
    },

    dateIsLessThanMonthsAgo (d, months) {
        const ago = moment().subtract(months, 'months')
        return moment(d).isAfter(ago)
    },

    handleError (e, message = undefined) {
        Vue.toasted.error(message ? message : e.body.message)
        //console.log('sentry capturing', process.env.VUE_APP_SENTRY_DSN);
        // Sentry.captureException(e)
    },

    formatSize (value) {
        if ((typeof (value) === 'number') && !isNaN(value)) {
            const neg = (value < 0)
            const units = ['', 'K', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

            if (neg) {
                value = -value
            }

            if (value < 1) {
                return (neg ? '-' : '') + value
            }
            let exponent = Math.min(Math.floor(Math.log(value) / Math.log(1000)), units.length - 1)
            value = (value / Math.pow(1000, exponent)).toFixed(0) * 1
            let unit = units[exponent]

            return (neg ? '-' : '') + value + ' ' + unit
        } else {
            return value
        }
    },

    toBoolean (value) {
        return value === 1 || value === '1' || value === true
    }

}
